/*------------------------------------------------------------------------------
   About      : Structured model for `templateCriteria` (epic DA-697, II-FE-4)

                `templateCriteria` is a map of derived-quantity code → predicate
                string, e.g. `{ "USAGE_DELTA_PERCENTAGE": ">=10" }`. The builder
                UI edits a list of conditions; this module is the only thing
                that knows how a condition and its stored string correspond.

                ⚠️ **The compound form carries `%s` literally.** quill's
                `ii-interfaces.ts` puts it plainly: "the `%s` token is
                substituted with the runtime value first, which is what makes
                compound predicates work: `\">10 && %s<60\"` with a value of
                28.9 …". So a two-bound predicate is
                `<op1><val1> && %s<op2><val2>` — the `%s` marks where the
                runtime value is injected for the second comparison. Emitting
                the second bound without it produces a predicate quill rejects
                and pingpong could not evaluate.

                Pure and catalog-driven: the operator list comes from
                `criteriaGrammar.operators`, never a hardcoded array.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { CriteriaGrammar, TemplateCriteria } from '../types/insightTypes'
import { isValidCriteriaExpression }         from './insightCatalogHelpers'

export interface CriteriaCondition {
  /** Stable key for React, since `variable` can be empty or duplicated mid-edit. */
  id        : string
  variable  : string
  operator  : string
  value     : string
  /** Present ⇒ this is a compound (two-bound) predicate. */
  operator2 ?: string
  value2    ?: string
}

let conditionSeq = 0

/** Ids are local to the editing session and never persisted. */
export function newCondition(over: Partial<CriteriaCondition> = {}): CriteriaCondition {
  conditionSeq += 1
  return {
    id       : `cond-${conditionSeq}`,
    variable : '',
    operator : '',
    value    : '',
    ...over,
  }
}

/** Exposed for tests so ids are predictable across cases. */
export function resetConditionSeq(): void {
  conditionSeq = 0
}

/**
 * Renders a condition as the string that gets stored.
 *
 * Returns '' for an incomplete condition rather than a half-predicate, so a
 * row the user is still filling in never reaches the payload.
 */
export function formatCriteriaExpression(
  condition : CriteriaCondition,
  grammar  ?: CriteriaGrammar,
): string {
  if (!condition.operator || condition.value === '') return ''

  const primary = `${condition.operator}${condition.value}`

  if (!condition.operator2 || condition.value2 === undefined || condition.value2 === '') {
    return primary
  }

  // The `%s` is mandatory here — it is where pingpong injects the runtime value
  // for the second comparison.
  const compoundToken = grammar?.compoundToken ?? '%s'
  return `${primary} && ${compoundToken}${condition.operator2}${condition.value2}`
}

/**
 * Parses a stored predicate back into a condition.
 *
 * Tolerant of whitespace variants (`">=10"`, `"> 10"`, `">10 && %s <= 90"`)
 * because these strings are also hand-authored in the insight sheet and via the
 * Strapi admin UI, not only by this builder.
 *
 * Returns undefined when the string does not parse, which the caller surfaces
 * as a raw-text fallback rather than silently dropping the predicate — losing
 * a criterion on load would change when the insight fires.
 */
export function parseCriteriaExpression(
  variable   : string,
  expression : string,
  grammar   ?: CriteriaGrammar,
): CriteriaCondition | undefined {

  const compoundToken = grammar?.compoundToken ?? '%s'
  const trimmed       = expression.trim()
  if (!trimmed) return undefined

  // Operators are matched longest-first so '<=' is never read as '<' followed
  // by a stray '='.
  const operators = [...(grammar?.operators ?? ['<=', '>=', '==', '!=', '<', '>'])]
    .sort((a, b) => b.length - a.length)

  const readBound = (chunk: string): { operator : string; value : string } | undefined => {
    const text = chunk.trim()
    const operator = operators.find((op) => text.startsWith(op))
    if (!operator) return undefined
    const value = text.slice(operator.length).trim()
    if (!value || Number.isNaN(Number(value))) return undefined
    return { operator, value }
  }

  const [firstChunk, ...rest] = trimmed.split('&&')

  const primary = readBound(firstChunk)
  if (!primary) return undefined

  if (!rest.length) {
    return newCondition({ variable, operator : primary.operator, value : primary.value })
  }

  // Strip the compound token before reading the second operator; without it the
  // chunk starts with '%s' and no operator would match.
  const secondChunk = rest.join('&&').trim().replace(compoundToken, '').trim()
  const secondary   = readBound(secondChunk)
  if (!secondary) return undefined

  return newCondition({
    variable,
    operator  : primary.operator,
    value     : primary.value,
    operator2 : secondary.operator,
    value2    : secondary.value,
  })
}

/** Explodes a stored map into editable rows, preserving order. */
export function criteriaToConditions(
  criteria : TemplateCriteria | null | undefined,
  grammar ?: CriteriaGrammar,
): { conditions : CriteriaCondition[]; unparsed : Record<string, string> } {

  const conditions : CriteriaCondition[] = []
  const unparsed   : Record<string, string> = {}

  for (const [variable, expression] of Object.entries(criteria ?? {})) {
    const parsed = parseCriteriaExpression(variable, expression, grammar)
    if (parsed) conditions.push(parsed)
    // Kept rather than dropped: a predicate this builder cannot model still
    // governs when the insight fires, so losing it silently changes behaviour.
    else unparsed[variable] = expression
  }

  return { conditions, unparsed }
}

/**
 * Collapses rows back into the stored map.
 *
 * Incomplete rows are skipped, and `unparsed` entries are merged back in so a
 * predicate the UI could not model survives a save untouched.
 *
 * One variable can appear only once — the stored shape is keyed by variable, so
 * a duplicate row would silently overwrite. `duplicateVariables` reports them
 * for the caller to block on rather than losing one behind the other.
 */
export function conditionsToCriteria(
  conditions : CriteriaCondition[],
  unparsed  ?: Record<string, string>,
  grammar   ?: CriteriaGrammar,
): { criteria : TemplateCriteria; duplicateVariables : string[] } {

  const criteria   : TemplateCriteria = { ...(unparsed ?? {}) }
  const seen       = new Set<string>()
  const duplicates = new Set<string>()

  for (const condition of conditions) {
    if (!condition.variable) continue

    const expression = formatCriteriaExpression(condition, grammar)
    if (!expression) continue

    if (seen.has(condition.variable)) duplicates.add(condition.variable)
    seen.add(condition.variable)

    criteria[condition.variable] = expression
  }

  return { criteria, duplicateVariables : [...duplicates] }
}

export interface ConditionValidation {
  /** Ready to persist. */
  complete : boolean
  /** Human-readable reason, or undefined when fine. */
  problem ?: string
}

/** Row-level validation, for inline feedback before save. */
export function validateCondition(
  condition : CriteriaCondition,
  grammar  ?: CriteriaGrammar,
): ConditionValidation {

  if (!condition.variable) return { complete : false, problem : 'Pick a variable' }
  if (!condition.operator) return { complete : false, problem : 'Pick an operator' }
  if (condition.value === '') return { complete : false, problem : 'Enter a value' }
  if (Number.isNaN(Number(condition.value))) {
    return { complete : false, problem : 'Value must be a number' }
  }

  const hasSecondOperator = !!condition.operator2
  const hasSecondValue    = condition.value2 !== undefined && condition.value2 !== ''

  // Half a second bound is a mistake, not a valid single bound: silently
  // dropping it would quietly widen when the insight fires.
  if (hasSecondOperator !== hasSecondValue) {
    return { complete : false, problem : 'Complete or remove the second bound' }
  }
  if (hasSecondValue && Number.isNaN(Number(condition.value2))) {
    return { complete : false, problem : 'Second value must be a number' }
  }

  const expression = formatCriteriaExpression(condition, grammar)
  if (!isValidCriteriaExpression(grammar, expression)) {
    return { complete : false, problem : `"${expression}" does not match the allowed grammar` }
  }

  return { complete : true }
}
